import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, PenNib, Sparkle, ShieldCheck, BookOpen, FilmSlate, MusicNote, ArrowRight, SignOut, CaretDown } from '@phosphor-icons/react';
import { WelcomeMusicPlayer } from './landing/WelcomeMusicPlayer';
import { WelcomeTour } from './landing/WelcomeTour';
import { WhatWritersAreSaying } from './landing/WhatWritersAreSaying';
import { ServicesSection } from './landing/ServicesSection';
import { BackToTop } from '../components/BackToTop';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Landing = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const isAdmin = Boolean(user?.is_admin);

  // Fire-and-forget anonymous visit tracking (no cookies, IP-hashed server-side)
  useEffect(() => {
    axios.post(`${API}/track-visit`, { path: '/welcome' }).catch(() => {});
  }, []);

  // Show the scroll-down hint while the visitor is still at the top.
  const [hasScrolled, setHasScrolled] = useState(false);
  // Interactive tour overlay
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 80) {
        setHasScrolled(true);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleScrollHint = () => {
    const target = document.getElementById('hero-end');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/welcome', { replace: true });
    }
  };

  const features = [
    {
      icon: PenNib,
      title: 'Preserves your voice',
      detail: 'Corrects grammar, punctuation, and style without flattening the way you write. Genre presets keep literary fiction sounding like fiction, not a corporate memo.',
    },
    {
      icon: Sparkle,
      title: 'Two minds, one verdict',
      detail: 'Cross-references OpenAI GPT-5.2 and Gemini 3 Pro so subtle issues — repetition, weak verbs, format slips — get caught before your editor sees them.',
    },
    {
      icon: MusicNote,
      title: 'Songwriter analysis mode',
      detail: 'Paste your lyrics and we will not "fix" them — slang, abbreviations, and intentional phrasing are kept verbatim. Instead you get a thoughtful critique of theme, structure, tone, rhyme, standout lines, and what to develop.',
    },
    {
      icon: ShieldCheck,
      title: 'Clean files, always',
      detail: 'Review every change side-by-side. Your downloaded file is pristine — no markup, no comments, just the corrected manuscript ready to send.',
    },
  ];

  const formats = [
    { icon: BookOpen, label: 'Manuscripts', detail: 'Novel · Memoir · Short fiction' },
    { icon: FilmSlate, label: 'Screenplays', detail: 'Feature · TV · Animation · Stage' },
    { icon: MusicNote, label: 'Song lyrics', detail: 'Preserved verbatim · Critique only' },
    { icon: FileText, label: 'Any format', detail: 'TXT · DOCX · PDF · FDX · Fountain' },
  ];

  return (
    <div className="w-full min-h-screen bg-background text-ivory" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-md border-b border-border" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto h-16 flex items-center justify-between px-6 md:px-10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-12 rounded-md border border-border bg-card flex items-center justify-center">
              <span className="font-serif text-gold text-lg leading-none tracking-tight">MP</span>
            </div>
            <span className="text-xl font-serif font-medium tracking-tight text-ivory">Manuscript Polish</span>
            <WelcomeMusicPlayer />
          </div>
          <nav className="flex items-center gap-2 md:gap-3">
            <Link to="/about" className="hidden md:inline-block text-sm text-bone hover:text-gold transition-colors px-3 py-2" data-testid="nav-about">About</Link>
            <Link to="/how-to" className="hidden md:inline-block text-sm text-bone hover:text-gold transition-colors px-3 py-2" data-testid="nav-how-to">How it works</Link>
            <Link to="/creative-partner" className="hidden md:inline-block text-sm text-gold hover:text-gold-dark transition-colors px-3 py-2 font-medium" data-testid="nav-creative-partner">Creative Partner</Link>
            {isAuthenticated ? (
              <>
                <Link
                  to="/"
                  className="text-sm font-medium tracking-wide px-4 py-2 rounded-lg bg-gold text-charcoal hover:bg-gold-dark transition-colors"
                  data-testid="nav-back-to-dashboard"
                >
                  Back to Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium tracking-wide px-4 py-2 rounded-lg text-ivory border border-border hover:border-rose-400 hover:text-rose-300 transition-colors inline-flex items-center gap-1.5"
                  data-testid="nav-logout"
                  title="Sign out"
                >
                  <SignOut size={14} weight="duotone" />
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium tracking-wide px-4 py-2 rounded-lg text-ivory border border-border hover:border-gold hover:text-gold transition-colors"
                  data-testid="nav-signin"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-medium tracking-wide px-4 py-2 rounded-lg bg-gold text-charcoal hover:bg-gold-dark transition-colors"
                  data-testid="nav-start"
                >
                  Start free
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 30% 20%, #C6A87C 0%, transparent 45%), radial-gradient(circle at 80% 70%, #C6A87C 0%, transparent 55%)'
        }} />
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-36 relative animate-fade-up">
          {/* Large brand tag */}
          <div className="mb-10" data-testid="hero-brand-tag">
            <div className="inline-flex items-center justify-center rounded-xl border border-gold/40 bg-card/60 px-6 py-4 shadow-[0_8px_32px_rgba(198,168,124,0.08)]">
              <span className="font-serif text-gold leading-none tracking-tight" style={{ fontSize: 'clamp(3rem, 7vw, 5rem)' }}>MP</span>
            </div>
          </div>
          <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-gold mb-6">For novelists & screenwriters</p>
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif font-light tracking-tight leading-[1.02] text-ivory mb-8">
            Proofreading that respects the <em className="text-gold not-italic font-normal">voice</em> you spent years finding.
          </h1>
          <p className="text-lg md:text-xl text-bone font-sans leading-relaxed max-w-2xl mb-10">
            Manuscript Polish is an AI proofreader built for long-form fiction and screenwriting — fast, quiet, and uncannily good at knowing what to fix and what to leave alone.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {isAuthenticated ? (
              <Button
                onClick={() => navigate('/')}
                className="rounded-lg font-medium text-sm tracking-wide px-7 py-4 bg-gold text-charcoal hover:bg-gold-dark border border-transparent"
                data-testid="hero-cta-back-to-dashboard"
              >
                Back to Dashboard
                <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/register')}
                className="rounded-lg font-medium text-sm tracking-wide px-7 py-4 bg-gold text-charcoal hover:bg-gold-dark border border-transparent"
                data-testid="hero-cta-start"
              >
                Start with 3,000 free words
                <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            )}
            <Button
              onClick={() => setTourOpen(true)}
              className="rounded-lg font-medium text-sm tracking-wide px-7 py-4 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
              data-testid="hero-cta-howto"
              title="Open the interactive tour"
            >
              See how it works
            </Button>
          </div>
          <p className="text-xs text-ash font-sans mt-6 tracking-wide">No card required · 3,000 free welcome words · Cancel anytime</p>

          {/* Scroll indicator — disappears after the user scrolls 80px */}
          <button
            type="button"
            onClick={handleScrollHint}
            aria-label="See more below"
            className={`absolute left-1/2 -translate-x-1/2 bottom-6 md:bottom-10 flex flex-col items-center gap-2 transition-all duration-500 ${hasScrolled ? 'opacity-0 pointer-events-none translate-y-3' : 'opacity-100'}`}
            data-testid="hero-scroll-hint"
          >
            <span className="text-[10px] font-medium uppercase tracking-[0.42em] text-gold/80">
              Discover more
            </span>
            <span className="relative flex h-12 w-7 items-start justify-center rounded-full border border-gold/40 bg-card/40 backdrop-blur-sm">
              <span className="mt-2 h-2 w-1 rounded-full bg-gold scroll-dot" aria-hidden="true" />
            </span>
            <CaretDown size={14} weight="bold" className="text-gold/80 animate-bounce" />
          </button>
        </div>
        <div id="hero-end" aria-hidden="true" />
      </section>

      {/* Services — all offerings selectable from the welcome page */}
      <ServicesSection isAuthenticated={isAuthenticated} isAdmin={isAdmin} />

      {/* Editor mock divider */}
      <section className="border-t border-border bg-card/40">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 panel-luxe overflow-hidden">
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-border">
              <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-ash mb-4">Original</p>
              <p className="font-serif text-lg leading-[1.85] text-bone">
                She walked quick across the room, opening the door very carefully and seeing him standing their, holding the letter what she had wrote.
              </p>
            </div>
            <div className="p-8 md:p-10">
              <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-gold mb-4">Corrected</p>
              <p className="font-serif text-lg leading-[1.85] text-ivory">
                She walked <span className="bg-[#3D2121] text-[#B87B7B] line-through px-1 rounded">quick</span><span className="bg-[#27382B] text-[#8CB397] px-1 rounded">quickly</span> across the room, opening the door <span className="bg-[#3D2121] text-[#B87B7B] line-through px-1 rounded">very</span> carefully and seeing him standing <span className="bg-[#3D2121] text-[#B87B7B] line-through px-1 rounded">their</span><span className="bg-[#27382B] text-[#8CB397] px-1 rounded">there</span>, holding the letter <span className="bg-[#3D2121] text-[#B87B7B] line-through px-1 rounded">what she had wrote</span><span className="bg-[#27382B] text-[#8CB397] px-1 rounded">she had written</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="mb-16 max-w-2xl">
          <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-gold mb-4">Why writers choose it</p>
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-ivory leading-[1.1]">
            Built for <em className="text-gold not-italic font-normal">long-form</em> work — not blog posts.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.title} className="panel-luxe p-8 hover:border-gold/40 transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 rounded-lg border border-border bg-background flex items-center justify-center mb-6">
                <f.icon size={22} weight="duotone" className="text-gold" />
              </div>
              <h3 className="text-xl font-serif font-normal text-ivory tracking-tight mb-3">{f.title}</h3>
              <p className="text-sm text-bone font-sans leading-[1.75]">{f.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Songwriter demo strip — lyrics preserved + analysis bullets fade in */}
      <section className="border-t border-border bg-gradient-to-b from-card/40 to-transparent">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="flex items-center gap-3 mb-8">
            <MusicNote size={20} weight="duotone" className="text-gold" />
            <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">Songwriter Mode · live example</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            {/* Lyrics — left */}
            <div className="panel-luxe p-6 md:p-8 animate-fade-up" data-testid="demo-lyrics">
              <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">Lyrics · Preserved</span>
                <span className="text-[10px] font-mono text-ash/70">no edits</span>
              </div>
              <div className="font-serif font-light text-ivory text-lg md:text-xl leading-[1.85] italic space-y-3">
                <p className="lyric-line" style={{ animationDelay: '0.15s' }}>walkin down the road tonite</p>
                <p className="lyric-line" style={{ animationDelay: '0.3s' }}>nothing feels quiet rite</p>
                <p className="lyric-line" style={{ animationDelay: '0.45s' }}>cos i seen the moon to bright</p>
                <p className="lyric-line" style={{ animationDelay: '0.6s' }}>thinkin of u all nite</p>
              </div>
            </div>

            {/* Analysis — right */}
            <div className="panel-luxe p-6 md:p-8 relative overflow-hidden" data-testid="demo-analysis">
              <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold">Analysis · AI critique</span>
                <span className="text-[10px] font-mono text-emerald-300/70">87/100 · overall</span>
              </div>
              <ul className="space-y-3.5 text-sm md:text-[15px] text-bone font-sans leading-[1.7]">
                {[
                  { tag: 'Theme', text: 'Restless longing under a sleepless night — strong evocative anchor.' },
                  { tag: 'Voice', text: 'Phonetic spelling ("tonite", "rite") preserves an intimate, demo-tape intimacy.' },
                  { tag: 'Rhyme', text: 'AABB pattern with near-rhyme ("bright/nite") — natural, never forced.' },
                  { tag: 'Develop', text: 'Bridge could introduce a turn — consider a tactile image to break the loop.' },
                ].map((row, i) => (
                  <li
                    key={row.tag}
                    className="flex gap-3 analysis-bullet"
                    style={{ animationDelay: `${0.7 + i * 0.2}s` }}
                  >
                    <span className="shrink-0 inline-block min-w-[64px] text-[10px] font-medium uppercase tracking-[0.22em] text-gold mt-1">
                      {row.tag}
                    </span>
                    <span>{row.text}</span>
                  </li>
                ))}
              </ul>
              <div
                className="absolute -bottom-1 -right-1 w-32 h-32 rounded-full bg-gold/5 blur-3xl pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>

          <p className="text-xs text-ash font-sans text-center mt-10 max-w-2xl mx-auto leading-relaxed">
            Your lyrics are returned <span className="text-ivory">verbatim</span> — slang, abbreviations, and stylistic choices stay exactly as you wrote them. The analysis sits separately, so it informs without interfering.
          </p>
        </div>
      </section>

      {/* Formats strip */}
      <section className="border-t border-border bg-card/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
            {formats.map((f) => (
              <div key={f.label} className="px-6 md:px-10 py-8 flex items-center gap-5" data-testid={`format-${f.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <f.icon size={32} weight="duotone" className="text-gold shrink-0" />
                <div>
                  <p className="text-lg font-serif font-normal text-ivory">{f.label}</p>
                  <p className="text-xs text-ash font-sans tracking-wide mt-1">{f.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creative Partner promo */}
      <section className="border-t border-border/60 bg-card/30" data-testid="cp-promo-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-4">{isAdmin ? 'Premium · Complimentary for the host' : 'Premium · £79 / year'}</p>
              <h2 className="text-4xl md:text-6xl font-serif font-light tracking-tight text-ivory leading-[1.05] mb-6">
                Creative <em className="text-gold not-italic font-normal">Partner</em>
              </h2>
              <p className="text-lg md:text-xl font-serif font-light text-bone leading-[1.5] mb-6">
                A personalised creative partner that helps turn ideas into finished, publishable projects —
                and remembers every step of your journey.
              </p>
              <p className="text-base text-ash font-sans leading-relaxed mb-8">
                Six expert partners — Maya, Alex, Samantha, Lawrence, Keith and Noah — on-call to
                brainstorm, challenge, edit, pitch and research alongside you.
              </p>
              <Link
                to="/creative-partner"
                className="inline-flex items-center gap-2 rounded-lg font-medium text-sm tracking-wide px-8 py-4 bg-gold text-charcoal hover:bg-gold-dark transition-colors"
                data-testid="cp-promo-cta"
              >
                Meet your partners
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'Maya',     title: 'Innovator',       c: 'text-gold',          ring: 'border-gold/40' },
                { name: 'Alex',     title: 'Architect',       c: 'text-emerald-300',   ring: 'border-emerald-400/40' },
                { name: 'Samantha', title: 'Wordsmith',       c: 'text-sky-300',       ring: 'border-sky-400/40' },
                { name: 'Lawrence', title: 'Analyst',         c: 'text-rose-300',      ring: 'border-rose-400/40' },
                { name: 'Keith',    title: 'Researcher',      c: 'text-violet-300',    ring: 'border-violet-400/40' },
                { name: 'Noah',     title: 'Progress Coach',  c: 'text-amber-300',     ring: 'border-amber-400/40' },
              ].map((p) => (
                <div key={p.name} className={`panel-luxe p-5 border ${p.ring}`}>
                  <p className="text-[9px] uppercase tracking-[0.28em] text-ash mb-1">The {p.title}</p>
                  <p className={`text-xl font-serif ${p.c}`}>{p.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What writers are saying — dynamic, admin-managed reviews */}
      <WhatWritersAreSaying />

      {/* CTA */}
      <section className="border-t border-border bg-card/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-ivory mb-6 leading-[1.1]">
            Your next draft, <em className="text-gold not-italic font-normal">cleaner</em>.
          </h2>
          <p className="text-base md:text-lg text-bone font-sans max-w-xl mx-auto mb-10 leading-relaxed">
            {isAuthenticated
              ? 'Your account is ready — head back to the dashboard to keep polishing.'
              : 'Sign up in thirty seconds. Your first 3,000 words are on the house — no card, no commitment.'}
          </p>
          <Button
            onClick={() => navigate(isAuthenticated ? '/' : '/register')}
            className="rounded-lg font-medium text-sm tracking-wide px-8 py-4 bg-gold text-charcoal hover:bg-gold-dark border border-transparent"
            data-testid="footer-cta-start"
          >
            {isAuthenticated ? 'Back to Dashboard' : 'Start proofreading free'}
            <ArrowRight size={16} weight="bold" className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-8 w-11 rounded-md border border-border bg-card flex items-center justify-center">
              <span className="font-serif text-gold text-sm leading-none tracking-tight">MP</span>
            </div>
            <span className="text-sm font-serif font-medium text-ivory">Manuscript Polish</span>
            <span className="text-xs text-ash ml-3 font-sans">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-bone font-sans">
            <Link to="/about" className="hover:text-gold transition-colors" data-testid="footer-about">About</Link>
            <Link to="/how-to" className="hover:text-gold transition-colors" data-testid="footer-howto">How it works</Link>
            <Link to="/login" className="hover:text-gold transition-colors" data-testid="footer-signin">Sign in</Link>
          </div>
        </div>
      </footer>

      <WelcomeTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onPrimaryCta={() => navigate(isAuthenticated ? '/' : '/register')}
      />
      <BackToTop />
    </div>
  );
};
;
